





































































































































import { Component, Vue, Watch } from 'vue-property-decorator';
import { State, Mutation } from 'vuex-class';
import validator from 'validator';
import isEmpty from 'lodash/isEmpty';
import { IUser } from '~/store/profile/types';
import { IWallet } from '~/store/inventory/types';
import BlueHeroBanner from '~/components/UIComponents/BlueHeroBanner.vue';
import getZendeskFieldsJson from '~/queries/getZendeskFieldsJson.gql';
import getZendeskDynamicContent from '~/queries/getZendeskDynamicContent.gql';
import CreateZendeskTicketMutation from '~/mutations/createZendeskTicket.gql';
import { getCurrentLocale } from '../utils/locales';

type ZendeskType = {
  id: number;
  tagValue: string;
  displayName: string;
  childTypes: ZendeskType[];
  fullyQualifiedName: string;
};

type DropdownType = {
  id: string;
  typeName: string;
  label: string;
  required: boolean;
  typeDropDown: boolean;
  items: any[];
  disabled: boolean;
  isLoading: boolean;
  currentName: string;
  hidden: boolean;
};

@Component({ components: { BlueHeroBanner } })
export default class ContactUs extends Vue {
  @State(profile => profile.user, { namespace: 'profile' })
  currentUser!: IUser;
  @State('wallets', { namespace: 'inventory' })
  private wallets!: IWallet[];

  @Mutation toggleErrorSnackbar!: (payload?: boolean) => void;
  @Mutation updateSnackbarErrorText!: (args: any) => void;

  language = 'en';
  localeCode = '';
  success = false;
  sending = false;
  formState: { [state: string]: string } = {
    name: '',
    email: '',
    address: '',
    description: '',
    discordHandle: '',
    selectedZendeskTypeName: '',
    selectedZendeskTypeValue: '',
    preferredLanguage: 'english',
  };
  zendeskFields = '';
  zendeskDynamicContent: Map<string, []> = new Map();

  zendeskTypeArray: ZendeskType[] = [];
  typeDropdowns: DropdownType[] = [
    {
      id: 'IssueType',
      typeName: 'IssueType',
      label: '',
      required: true,
      typeDropDown: true,
      items: [],
      disabled: false,
      isLoading: true,
      currentName: '',
      hidden: false,
    },
  ];
  typeNameToDropdownIndex: Map<string, number> = new Map();

  preferredLanguageDropdown = {
    id: 'preferredLanguage',
    label: '',
    autoFocus: false,
    required: false,
    typeDropDown: false,
    dropDown: true,
    textArea: false,
    disabled: false,
    hidden: true,
    items: [],
    isLoading: isEmpty(this.zendeskFields),
    hint: '',
  };

  get typeFields() {
    return this.typeDropdowns;
  }

  get formFields() {
    this.buildTypeDropdowns();
    this.buildLanguageDropdown();
    return [
      {
        id: 'name',
        label: this.$t('pages.contactSupport.name') as string,
        autoFocus: !this.currentUser.loggedIn,
        required: true,
        typeDropDown: false,
        dropDown: false,
        textArea: false,
        disabled: false,
        hidden: false,
        items: [],
        isLoading: false,
        hint: '',
      },
      {
        id: 'email',
        label: this.$t('pages.contactSupport.email') as string,
        autoFocus: false,
        required: true,
        typeDropDown: false,
        dropDown: false,
        textArea: false,
        disabled: false,
        hidden: false,
        items: [],
        isLoading: false,
        hint: '',
      },
      {
        id: 'address',
        label: this.$t('pages.contactSupport.address'),
        autoFocus: false,
        required: false,
        typeDropDown: false,
        dropDown: false,
        textArea: false,
        disabled: false,
        hidden: false,
        items: [],
        isLoading: false,
        hint: this.$t('pages.contactSupport.addressHint'),
      },
      {
        id: 'discordHandle',
        label: this.$t('pages.contactSupport.discordHandle'),
        autoFocus: false,
        required: false,
        typeDropDown: false,
        dropDown: false,
        textArea: false,
        disabled: false,
        hidden: false,
        items: [],
        isLoading: false,
        hint: '',
      },
      this.preferredLanguageDropdown,
      {
        id: 'zenType',
        label: this.$t('pages.contactSupport.type'),
        autoFocus: false,
        required: true,
        typeDropDown: true,
        dropDown: false,
        textArea: false,
        disabled: false,
        hidden: false,
        items: [],
        isLoading: isEmpty(this.zendeskFields),
        hint: '',
      },
      {
        id: 'description',
        label: this.$t('pages.contactSupport.description') as string,
        autoFocus: false,
        required: true,
        typeDropDown: false,
        dropDown: false,
        textArea: true,
        disabled: false,
        hidden: false,
        items: [],
        isLoading: false,
        hint: '',
      },
    ];
  }

  get confirmationText() {
    return {
      main: this.$t('pages.contactSupport.confirmationText') as string,
      note: `<span class="font-weight-bold">${this.$t(
        'pages.contactSupport.confirmationNoteLine1.string',
      )}</span>${this.$t('pages.contactSupport.confirmationNoteLine2.string')}`,
    };
  }

  get galaAddress() {
    if (this.wallets && this.wallets.length) {
      const galaWallet = this.wallets.find(wallet => wallet.symbol === 'GALA');
      return galaWallet ? galaWallet.receiveAddress : '';
    }
    return '';
  }

  get btnDisabled() {
    const {
      name,
      email,
      description,
      selectedZendeskTypeValue,
    } = this.formState;
    const requiredValues = [name, email, description, selectedZendeskTypeValue];

    return requiredValues.some(val => !val);
  }

  created() {
    const localeInfo = {
      localeCookie: this.$i18n.getLocaleCookie(),
      browserLocale: this.$i18n.getBrowserLocale(),
      locales: this.$i18n.locales,
    };
    const currentLocale = getCurrentLocale(localeInfo);
    this.language = currentLocale.code;
    this.localeCode = this.$t('pages.contactSupport.name') as string;
  }

  async mounted() {
    await this.$recaptcha.init();

    await this.fetchZendeskDynamicContent();
    await this.fetchZendeskFields();
    this.buildZendeskTypes();
  }

  async beforeDestroy() {
    await this.$recaptcha.destroy();
  }

  validateEmail(input: string) {
    const isValid = validator.isEmail(input);
    if (isValid) {
      return true;
    }

    return false;
  }

  async fetchZendeskFields() {
    const apolloClient = this.$apolloProvider.clients.gateway;
    const response = await apolloClient.query({
      query: getZendeskFieldsJson,
      variables: { platform: 'games' },
    });
    const zendeskFields = response.data.getZendeskFields;
    this.zendeskFields = zendeskFields;
    return this.zendeskFields;
  }

  async fetchZendeskDynamicContent() {
    const apolloClient = this.$apolloProvider.clients.gateway;
    const response = await apolloClient.query({
      query: getZendeskDynamicContent,
    });
    const zendeskDynamicContent = response.data.getZendeskDynamicContent;
    const dynamicContentJson = JSON.parse(zendeskDynamicContent);
    dynamicContentJson.forEach((dynamicContent: any) => {
      this.zendeskDynamicContent.set(
        dynamicContent.placeholder,
        dynamicContent.variants,
      );
    });

    return this.zendeskDynamicContent;
  }

  loadingText(isLoading: boolean) {
    if (isLoading) {
      return `${this.$t('common.cta.loading')}...`;
    } else {
      return this.$t('common.cta.noData');
    }
  }

  clearForm() {
    this.formState.description = '';
  }

  getSubjectFromZendeskType(typeName: string): string {
    let subject = '';
    const typeNames = typeName.split('::');
    typeNames.forEach((type, index) => {
      if (index === 0) {
        subject = `${this.getTranslatedDisplayName(type, true)}`;
      } else {
        subject += ` | ${this.getTranslatedDisplayName(type, true)}`;
      }
    });

    return subject;
  }

  async handleSubmit() {
    if (!this.validateEmail(this.formState.email)) {
      this.updateSnackbarErrorText(
        this.$t('common.notifications.enterValidEmail') as string,
      );
      this.toggleErrorSnackbar();

      return;
    }
    if (!this.btnDisabled) {
      try {
        this.sending = true;
        const apolloClient = this.$apolloProvider.clients.gateway;

        const {
          name,
          email,
          address,
          description,
          discordHandle,
          selectedZendeskTypeName,
          selectedZendeskTypeValue,
          preferredLanguage,
        } = this.formState;

        const subjectValue = this.getSubjectFromZendeskType(
          selectedZendeskTypeName,
        );

        const botToken = await this.$recaptcha.execute('submitTicket');

        const variables: { zendeskTicket: any } = {
          zendeskTicket: {
            name,
            emailAddress: email,
            galaAddress: address,
            subject: subjectValue,
            description,
            language: preferredLanguage,
            issueType: selectedZendeskTypeValue,
            discordHandle,
            platform: 'games',
            botToken: botToken,
          },
        };

        const { data } = await apolloClient.mutate({
          mutation: CreateZendeskTicketMutation,
          variables,
        });

        this.sending = false;
        if (
          data &&
          data.createZendeskTicket &&
          data.createZendeskTicket.success
        ) {
          // UA tracking
          this.$ua.trackContactSupportRequestCompleteEvent(data);

          this.success = true;
          this.clearForm();
        } else {
          throw new Error(
            this.$t('pages.contactSupport.ticketError') as string,
          );
        }
      } catch (error) {
        // UA tracking
        this.$ua.trackContactSupportRequestErrorEvent(error);

        this.$sentry.captureException(error);
        this.sending = false;
        console.warn(error);
        this.updateSnackbarErrorText(
          this.$t('pages.contactSupport.ticketError') as string,
        );
        this.toggleErrorSnackbar();
      }
    }
  }

  getTypeValueFromName(name: string) {
    const fieldsJson = JSON.parse(this.zendeskFields);
    const fields = fieldsJson.find(
      (ticketField: { name: string }) => ticketField.name === 'games',
    );

    let returnValue = '';

    fields?.custom_field_options?.forEach(
      (typeField: { raw_name: string; value: string }) => {
        if (name === typeField.raw_name) {
          returnValue = typeField.value;
        }
      },
    );

    return returnValue;
  }

  buildZendeskTypes() {
    const fieldsJson = JSON.parse(this.zendeskFields);
    const fields = fieldsJson.find(
      (ticketField: { name: string }) => ticketField.name === 'games',
    );

    fields?.custom_field_options?.forEach(
      (typeField: {
        name: string;
        id: number;
        value: string;
        raw_name: string;
      }) => {
        this.buildZendeskType(
          typeField.raw_name,
          typeField.id,
          typeField.value,
          typeField.raw_name,
        );
      },
    );
  }

  buildZendeskType(
    typeName: string,
    typeId: number,
    tagValue: string,
    rawName: string,
  ) {
    if (this.zendeskFields === '') {
      return;
    }
    const typeNames = typeName.split('::');
    let zendeskTypes: ZendeskType[] = this.zendeskTypeArray;
    let fullName = 'IssueType';
    typeNames.forEach((displayName, key, arr) => {
      // last iteration - this is the child
      if (key === arr.length - 1) {
        const zendeskType: ZendeskType = {
          id: typeId,
          tagValue: tagValue,
          fullyQualifiedName: typeName,
          displayName: displayName,
          childTypes: [],
        };
        zendeskTypes.push(zendeskType);
      } else {
        const parentType = this.findType(zendeskTypes, displayName);
        if (parentType === undefined) {
          fullName = `${fullName}::${displayName}`;
          const zendeskType: ZendeskType = {
            id: 0,
            tagValue: fullName,
            fullyQualifiedName: fullName,
            displayName: displayName,
            childTypes: [],
          };

          zendeskTypes.push(zendeskType);

          zendeskTypes = zendeskType.childTypes;
        } else {
          fullName = parentType.fullyQualifiedName;
          zendeskTypes = parentType.childTypes;
        }
      }
    });
  }

  buildTypeDropdowns() {
    if (this.zendeskFields === '') {
      return;
    }

    this.formState.selectedZendeskTypeName = '';
    this.formState.selectedZendeskTypeValue = '';

    this.typeDropdowns = [];
    this.typeDropdowns.push(
      ...this.buildTypeDropdown(this.zendeskTypeArray, 'IssueType', ''),
    );
    this.typeDropdowns.reverse();
    this.typeDropdowns.forEach((value, index) => {
      this.typeNameToDropdownIndex.set(value.id, index);
    });
  }

  getTranslatedDisplayName(
    typeName: string,
    defaultLanguage: boolean = false,
  ): string {
    let displayName = '';
    let defaultName = '';
    const currentLocale = this.$i18n.locale.toLocaleLowerCase();
    const dynamicContents = this.zendeskDynamicContent.get(typeName);
    dynamicContents?.forEach(
      (dynamicContent: {
        content: string;
        locale: string;
        locale_id: number;
      }) => {
        const localeLower = dynamicContent.locale.toLocaleLowerCase();
        if (
          localeLower === currentLocale ||
          localeLower.startsWith(currentLocale)
        ) {
          displayName = dynamicContent.content;
        }

        if (dynamicContent.locale_id === 1) {
          defaultName = dynamicContent.content;
        }
      },
    );

    if (displayName === '' || defaultLanguage) {
      if (defaultName === '') {
        displayName = typeName;
      } else {
        displayName = defaultName;
      }
    }

    return displayName;
  }

  buildTypeDropdown(
    typeArray: ZendeskType[],
    dropdownId: string,
    tagline: string,
  ) {
    const typeItems: any[] = [];
    const typeDropdowns: any[] = [];

    typeArray.forEach(zendeskType => {
      const translatedDisplayName = this.getTranslatedDisplayName(
        zendeskType.displayName,
      );
      typeItems.push({
        text: translatedDisplayName,
        value: zendeskType.fullyQualifiedName,
      });

      if (zendeskType.childTypes.length > 0) {
        typeDropdowns.push(
          ...this.buildTypeDropdown(
            zendeskType.childTypes,
            zendeskType.fullyQualifiedName,
            zendeskType.fullyQualifiedName,
          ),
        );
      }
    });

    let taglineDisplay = tagline.replaceAll('IssueType::', '');
    taglineDisplay = taglineDisplay.replaceAll('::', ' > ');

    const typeDropDown = {
      id: dropdownId,
      typeName: dropdownId,
      label: taglineDisplay,
      required: true,
      typeDropDown: true,
      items: typeItems,
      disabled: false,
      isLoading: false,
      currentName: '',
      hidden: true,
    };

    if (dropdownId === 'IssueType') {
      typeDropDown.hidden = false;
    }

    typeDropdowns.push(typeDropDown);

    return typeDropdowns;
  }

  findType(
    zendeskTypes: ZendeskType[] | undefined,
    typeName: string,
  ): ZendeskType | undefined {
    if (zendeskTypes === undefined) {
      return undefined;
    }

    let returnVal: ZendeskType | undefined = undefined;

    zendeskTypes.forEach(zendeskType => {
      if (zendeskType.displayName === typeName) {
        returnVal = zendeskType;
        return;
      }
    });

    return returnVal;
  }

  buildLanguageDropdown() {
    if (this.zendeskFields === '') {
      return;
    }

    const fieldsJson = JSON.parse(this.zendeskFields);
    const fields = fieldsJson.find(
      (ticketField: { name: string }) => ticketField.name === 'language',
    );

    const languageOptions: any = [];
    fields.custom_field_options.forEach((option: any) => {
      languageOptions.push({
        name: this.getTranslatedDisplayName(option.raw_name),
        value: option.value,
      });
    });

    this.preferredLanguageDropdown.label = this.getTranslatedDisplayName(
      fields.raw_title,
    );
    this.preferredLanguageDropdown.items = languageOptions;
    this.preferredLanguageDropdown.isLoading = false;
    this.preferredLanguageDropdown.hidden = false;
  }

  @Watch('currentUser', { deep: true, immediate: true })
  handleAutoFillUserInfo(currentUser: IUser) {
    const { firstName, lastName, email } = currentUser;
    if (firstName && lastName && email) {
      this.formState.name = `${firstName} ${lastName}`;
      this.formState.email = email;
    }
  }

  @Watch('galaAddress', { immediate: true })
  handleAutoFillAddress(galaAddress: string) {
    if (galaAddress) {
      this.formState.address = galaAddress;
    }
  }

  getEndOfBranchTypeName(typeName: string): string {
    let endOfBranch = typeName;
    const childIndex = this.typeNameToDropdownIndex.get(typeName);

    if (childIndex) {
      const childDropdown = this.typeDropdowns[childIndex];
      if (
        childDropdown.currentName !== '' &&
        childDropdown.currentName.startsWith('IssueType')
      ) {
        endOfBranch = this.getEndOfBranchTypeName(childDropdown.currentName);
      }
    }

    return endOfBranch;
  }

  handleZendeskTypeChange(selectedTypeName: any) {
    this.formState.selectedZendeskTypeName = '';
    this.formState.selectedZendeskTypeValue = '';

    let endOfBranchTypeName = '';

    if (!selectedTypeName.startsWith('IssueType')) {
      this.formState.selectedZendeskTypeValue = this.getTypeValueFromName(
        selectedTypeName,
      );
      endOfBranchTypeName = `IssueType::${selectedTypeName}`;
    } else {
      endOfBranchTypeName = this.getEndOfBranchTypeName(selectedTypeName);
    }

    this.typeDropdowns.forEach(typeDropdown => {
      if (endOfBranchTypeName.startsWith(typeDropdown.id)) {
        typeDropdown.hidden = false;
        if (!typeDropdown.currentName.startsWith('IssueType')) {
          this.formState.selectedZendeskTypeName = typeDropdown.currentName;
          this.formState.selectedZendeskTypeValue = this.getTypeValueFromName(
            typeDropdown.currentName,
          );
        }
      } else {
        typeDropdown.hidden = true;
      }
    });
  }
}
